import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";

export default function InvestorHealthWidget() {
  const { data: investors = [], isLoading } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list("-relationship_health_score"),
  });

  // Filter investors with scores and categorize
  const scoredInvestors = investors.filter(inv => 
    inv.relationship_health_score !== undefined && 
    inv.relationship_health_score !== null
  );

  const atRiskInvestors = scoredInvestors.filter(inv => inv.relationship_health_score < 50);
  const goodHealthInvestors = scoredInvestors.filter(inv => inv.relationship_health_score >= 80);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-600 bg-emerald-50";
    if (score >= 60) return "text-amber-600 bg-amber-50";
    if (score >= 40) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
          <Activity className="w-5 h-5 text-zinc-900" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Relasjonshelse</h3>
          <p className="text-sm text-slate-500">{scoredInvestors.length} investorer med score</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : scoredInvestors.length === 0 ? (
        <div className="text-center py-8">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Ingen helsescorer beregnet ennå</p>
        </div>
      ) : (
        <>
          {/* At Risk Alert */}
          {atRiskInvestors.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-semibold text-red-900">
                  {atRiskInvestors.length} investor(er) trenger oppmerksomhet
                </span>
              </div>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 bg-emerald-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-emerald-600">{goodHealthInvestors.length}</div>
              <div className="text-xs text-slate-600">Utmerket</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-amber-600">
                {scoredInvestors.filter(i => i.relationship_health_score >= 60 && i.relationship_health_score < 80).length}
              </div>
              <div className="text-xs text-slate-600">God</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{atRiskInvestors.length}</div>
              <div className="text-xs text-slate-600">Risiko</div>
            </div>
          </div>

          {/* Top/Bottom Investors */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Topp investorer</h4>
            {scoredInvestors.slice(0, 5).map((investor) => (
              <Link
                key={investor.id}
                to={createPageUrl("Investors")}
                className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{investor.name}</p>
                  <p className="text-xs text-slate-500">{investor.type}</p>
                </div>
                <Badge className={`ml-2 ${getScoreColor(investor.relationship_health_score)}`}>
                  {investor.relationship_health_score}
                </Badge>
              </Link>
            ))}
          </div>

          {/* At Risk Investors */}
          {atRiskInvestors.length > 0 && (
            <div className="space-y-2 mt-4">
              <h4 className="text-sm font-semibold text-red-700 mb-2">Trenger oppfølging</h4>
              {atRiskInvestors.slice(0, 3).map((investor) => (
                <Link
                  key={investor.id}
                  to={createPageUrl("Investors")}
                  className="flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{investor.name}</p>
                    <p className="text-xs text-slate-500">{investor.type}</p>
                  </div>
                  <Badge className="ml-2 bg-red-100 text-red-700">
                    {investor.relationship_health_score}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}